import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { X, Search } from "lucide-react";
import { cn } from "@/lib/utils";

type MemberOption = { id: string; full_name: string };

interface LeaderSelectProps {
  selected: MemberOption[];
  onChange: (members: MemberOption[]) => void;
  churchId?: string;
  className?: string;
}

export default function LeaderSelect({ selected, onChange, churchId, className }: LeaderSelectProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: members = [] } = useQuery({
    queryKey: ["members_for_leader", churchId],
    enabled: !!churchId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name")
        .eq("church_id", churchId!)
        .order("full_name");
      if (error) throw error;
      return data as MemberOption[];
    },
  });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = members.filter(
    (m) =>
      !selected.some((s) => s.id === m.id) &&
      m.full_name.toLowerCase().includes(search.toLowerCase())
  );

  const remove = (id: string) => onChange(selected.filter((s) => s.id !== id));
  const add = (m: MemberOption) => {
    onChange([...selected, m]);
    setSearch("");
  };

  const getInitials = (name: string) =>
    name
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase())
      .join("")
      .slice(0, 2);

  return (
    <div ref={wrapperRef} className={cn("relative", open && "z-[120]", className)}>
      <div className="flex flex-wrap items-center gap-1.5 min-h-[36px] border border-input rounded-md bg-background px-2.5 py-1.5">
        {selected.map((s) => (
          <Badge key={s.id} variant="secondary" className="text-xs gap-1 pr-1">
            {s.full_name}
            <button type="button" onClick={() => remove(s.id)} className="hover:text-destructive">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        <div className="flex items-center gap-1 flex-1 min-w-[100px]">
          <Search className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={selected.length ? "Add more..." : "Search leaders..."}
            className="h-6 border-0 p-0 text-xs focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
          />
        </div>
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute left-0 top-full z-[130] mt-1 w-full max-h-[200px] overflow-y-auto rounded-md border bg-popover shadow-lg">
          {filtered.slice(0, 20).map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => add(m)}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-foreground hover:bg-accent/70 active:bg-accent transition-colors text-left border-b border-border/40 last:border-b-0"
            >
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarFallback className="bg-muted text-primary text-[11px] font-semibold">
                  {getInitials(m.full_name) || "--"}
                </AvatarFallback>
              </Avatar>
              {m.full_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
